import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Avatar, AvatarFallback } from './ui/avatar';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { 
  X, 
  Send, 
  Circle,
  ChevronDown,
  Minimize2,
  Maximize2,
  Image as ImageIcon
} from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DirectMessagePanel = ({ member, onClose, onMessagesRead }) => {
  const { user, token } = useAuth();
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const initConversation = useCallback(async () => {
    // Prevent starting conversation with yourself
    if (member.id === user?.id) {
      toast.error('Cannot start a conversation with yourself');
      if (onClose) onClose();
      return;
    }
    
    try {
      setLoading(true);
      // Create or get existing conversation
      const convResponse = await fetch(`${API}/conversations/${member.id}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        }
      });
      
      if (convResponse.ok) {
        const conv = await convResponse.json();
        // Store conversation with normalized id field
        setConversation({ id: conv.conversation_id, ...conv });
        
        // Fetch messages (this also marks them as read on the backend)
        const msgResponse = await fetch(`${API}/conversations/${conv.conversation_id}/messages`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (msgResponse.ok) {
          const msgs = await msgResponse.json();
          setMessages(msgs);
          // Notify parent that messages were read
          if (onMessagesRead) {
            onMessagesRead();
          }
        }
      } else {
        const errData = await convResponse.json().catch(() => ({}));
        toast.error(errData.detail || 'Failed to start conversation');
        if (onClose) onClose();
      }
    } catch (error) {
      console.error('Error initializing conversation:', error);
      toast.error('Failed to load conversation');
    } finally {
      setLoading(false);
    }
  }, [member.id, user?.id, token, onMessagesRead, onClose]);

  const fetchMessages = useCallback(async () => {
    if (!conversation) return;
    
    try {
      const response = await fetch(`${API}/conversations/${conversation.id}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const msgs = await response.json();
        setMessages(msgs);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  }, [conversation, token]);

  useEffect(() => {
    initConversation();
  }, [initConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!conversation) return;
    
    // Poll for new messages every 5 seconds
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, [conversation, fetchMessages]);

  useEffect(() => {
    if (!minimized && inputRef.current) {
      inputRef.current.focus();
    }
  }, [minimized]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !conversation) return;

    setSending(true);
    try {
      const response = await fetch(`${API}/conversations/${conversation.id}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ content: newMessage })
      });

      if (response.ok) {
        const msg = await response.json();
        setMessages(prev => [...prev, msg]);
        setNewMessage('');
      } else {
        toast.error('Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarColor = (name) => {
    const colors = [
      'bg-red-500', 'bg-orange-500', 'bg-amber-500', 
      'bg-emerald-500', 'bg-teal-500', 'bg-cyan-500',
      'bg-blue-500', 'bg-indigo-500', 'bg-violet-500',
      'bg-purple-500', 'bg-fuchsia-500', 'bg-pink-500'
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  if (minimized) {
    return (
      <div 
        className="fixed bottom-4 right-4 z-50 bg-card border shadow-xl rounded-xl cursor-pointer hover:shadow-2xl transition-shadow max-w-[calc(100%-2rem)] sm:max-w-none"
        onClick={() => setMinimized(false)}
        data-testid="dm-panel-minimized"
      >
        <div className="flex items-center gap-3 p-3">
          <div className="relative">
            <Avatar className={`h-8 w-8 ${getAvatarColor(member.name)}`}>
              <AvatarFallback className="text-white text-xs">
                {getInitials(member.name)}
              </AvatarFallback>
            </Avatar>
            {member.is_online && (
              <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-background rounded-full" />
            )}
          </div>
          <div>
            <p className="font-medium text-sm">{member.name}</p>
            <p className="text-xs text-muted-foreground">Click to expand</p>
          </div>
          <Maximize2 className="w-4 h-4 text-muted-foreground ml-2" />
        </div>
      </div>
    );
  }

  return (
    <div 
      className="fixed bottom-0 right-0 sm:bottom-4 sm:right-4 z-50 w-full sm:w-80 md:w-96 bg-card border shadow-2xl sm:rounded-xl flex flex-col overflow-hidden max-h-[70vh] sm:max-h-[500px]"
      data-testid="dm-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b bg-muted/30">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Avatar className={`h-9 w-9 ${getAvatarColor(member.name)}`}>
              <AvatarFallback className="text-white text-sm">
                {getInitials(member.name)}
              </AvatarFallback>
            </Avatar>
            {member.is_online && (
              <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-background rounded-full" />
            )}
          </div>
          <div>
            <p className="font-medium text-sm">{member.name}</p>
            <div className="flex items-center gap-1">
              <Circle 
                className={`w-2 h-2 ${member.is_online ? 'fill-green-500 text-green-500' : 'fill-gray-400 text-gray-400'}`} 
              />
              <span className={`text-xs ${member.is_online ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`}>
                {member.is_online ? 'Online' : 'Offline'}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-8 w-8"
            onClick={() => setMinimized(true)}
          >
            <Minimize2 className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-8 w-8"
            onClick={onClose}
            data-testid="close-dm-panel"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-3">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-pulse text-muted-foreground text-sm">Loading...</div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <p className="text-sm">No messages yet</p>
            <p className="text-xs">Start the conversation!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map((msg) => {
              const isMe = msg.sender_id === user?.id;
              return (
                <div
                  key={msg.id}
                  className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                      isMe 
                        ? 'bg-primary text-primary-foreground rounded-br-md' 
                        : 'bg-muted rounded-bl-md'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                    <p className={`text-[10px] mt-1 ${isMe ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                      {formatDistanceToNow(parseISO(msg.created_at), { addSuffix: true })}
                    </p>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        )}
      </ScrollArea>

      {/* Input */}
      <form onSubmit={handleSendMessage} className="p-3 border-t bg-muted/30">
        <div className="flex items-center gap-2">
          <Input
            ref={inputRef}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1"
            disabled={sending || loading}
            data-testid="dm-message-input"
          />
          <Button 
            type="submit" 
            size="icon"
            disabled={!newMessage.trim() || sending || loading}
            data-testid="dm-send-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </form>
    </div>
  );
};

export default DirectMessagePanel;
